/* Per-cell reveal-flip timing. The just-submitted row flips its tiles
   from outside-in in mirror pairs: pair 0 (the two end cells) goes first,
   then pair 1, ... down to the pivot pair last. Each tile rotates around
   its horizontal axis (rotateX 0 → 90 → 0); the mark class is applied at
   the 90° midpoint so the colour swap is hidden behind the edge-on tile.
   Confetti for new-green cells fires at flip-end. */

/** Duration of a single tile's flip, ms. */
export const REVEAL_FLIP_MS = 400;

/** Time between successive pair starts, ms. Pairs reveal sequentially —
    each pair finishes its full flip before the next starts — so the
    stagger equals the flip duration. */
export const REVEAL_STAGGER_MS = REVEAL_FLIP_MS;

/** Halfway through the flip — the rotateX(90°) edge-on moment. The mark
    class is applied here so the colour change is invisible. */
export const REVEAL_HALF_MS = REVEAL_FLIP_MS / 2;

/** Number of mirror pairs in a palindrome of `letterCount`. The pivot
    of an odd-length palindrome counts as its own pair. */
export function numPairs(letterCount: number): number {
  return Math.floor((letterCount + 1) / 2);
}

/** Total wall-clock time for a reveal to settle, ms. Pair at slot k
    starts at k × stagger and ends at k × stagger + flip; with stagger
    = flip that's slotCount × stagger. `slotCount` is the number of
    pairs that actually animate (unchanged pairs don't reserve a slot). */
export function revealTotalMs(slotCount: number): number {
  return slotCount * REVEAL_STAGGER_MS;
}

/** Outside-in pair index for a letter in a palindrome of `letterCount`.
    Outermost pair (cells 0 and N-1) is 0; pivot is the highest index. */
export function pairIndex(letterIndex: number, letterCount: number): number {
  return Math.min(letterIndex, letterCount - 1 - letterIndex);
}

/** Diff a guess against the previous guess to find letter indices the
    player has actually written or changed. The first guess (`prev` is
    null) treats every position as changed. Carried-forward greens that
    the player did not retype are unchanged and skip the flip — they
    snap to their final mark immediately on submit. */
export function changedLetterIndices(
  current: readonly (string | null)[],
  prev: readonly (string | null)[] | null,
): Set<number> {
  const out = new Set<number>();
  for (let i = 0; i < current.length; i++) {
    if (prev == null || current[i] !== prev[i]) out.add(i);
  }
  return out;
}

/** Map pair-index → slot-index for pairs that need to animate. The
    outside-in cadence skips unchanged pairs entirely: only pairs in the
    change set consume a stagger slot, so a guess that only retyped the
    centre flips just the centre pair without burning time on the
    untouched outer pairs. A pair animates if either of its two letter
    positions is in `changed`. */
export function buildRevealSlots(
  changed: ReadonlySet<number>,
  letterCount: number,
): Map<number, number> {
  const slots = new Map<number, number>();
  let nextSlot = 0;
  for (let p = 0; p < numPairs(letterCount); p++) {
    const left = p;
    const right = letterCount - 1 - p;
    if (changed.has(left) || changed.has(right)) {
      slots.set(p, nextSlot++);
    }
  }
  return slots;
}

/** Delay before the flip starts for a given letter index, ms. Returns
    null when the letter is in an unchanged pair — that cell gets no
    flip animation, and its mark applies immediately. */
export function revealDelayFor(
  letterIndex: number,
  letterCount: number,
  slots: ReadonlyMap<number, number>,
): number | null {
  const slot = slots.get(pairIndex(letterIndex, letterCount));
  return slot == null ? null : slot * REVEAL_STAGGER_MS;
}

/** True when the user has requested reduced motion — skip the flip. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
